import {
	type AttributeMapping,
	type FieldRule,
	MAPPED_USER_FIELDS,
	MappedUserField,
	type MappingPreviewFieldDto,
	TransformType,
} from '@today/types'
import { cn } from '@today/ui-primitives'
import { AttributeInput } from './attribute-input.view'
import { FIELD_LABELS, previewValueLabel } from './attribute-mapping-labels'
import { TransformEditor } from './transform-editor.view'

/**
 * Props for {@link FieldMappingTable}.
 *
 * @property fields - The draft's field rules; `null` = unmapped.
 * @property attributeNames - The sample's attribute names, offered by each combobox.
 * @property preview - The server preview's per-field results, when there is one.
 * @property onChange - Reports one field's new rule, or `null` when its attribute is cleared.
 * @property errors - Refusals located on a field (`422`), shown next to its transform.
 * @property isDisabled - Disables every control.
 */
export interface FieldMappingTableProps {
	fields: AttributeMapping['fields']
	attributeNames: string[]
	preview?: MappingPreviewFieldDto[] | null
	onChange: (field: MappedUserField, rule: FieldRule | null) => void
	errors?: Partial<Record<MappedUserField, string>>
	isDisabled?: boolean
}

const NONE = { type: TransformType.NONE }

type RowProps = Omit<FieldMappingTableProps, 'fields' | 'preview' | 'errors'> & {
	field: MappedUserField
	rule: FieldRule | null
	previewEntry: MappingPreviewFieldDto | undefined
	error: string | undefined
}

/** One TODAY field: attribute, transform, preview value and the required flag. */
function FieldRow({ field, rule, attributeNames, previewEntry, onChange, error, isDisabled }: RowProps) {
	const label = FIELD_LABELS[field]
	const isEmail = field === MappedUserField.EMAIL
	const missing = isEmail && !rule
	const setAttribute = (name: string) =>
		onChange(field, name === '' ? null : { idpAttribute: name, transform: rule?.transform ?? NONE })
	return (
		<tr
			className={cn('border-t border-ds-border align-top', missing && 'bg-ds-danger/10')}
			aria-label={label}
			aria-invalid={missing || undefined}
		>
			<th scope="row" className="py-2 pr-2 text-left font-medium text-ds-text">
				{label}
			</th>
			<td className="py-2 pr-2">
				<AttributeInput
					label={`IdP attribute for ${label}`}
					value={rule?.idpAttribute ?? ''}
					options={attributeNames}
					onChange={setAttribute}
					isInvalid={missing}
					isDisabled={isDisabled}
				/>
				{missing ? <p className="text-ds-sm text-ds-danger">Email must be mapped</p> : null}
			</td>
			<td className="py-2 pr-2">
				{rule ? (
					<TransformEditor
						label={label}
						value={rule.transform}
						error={error}
						isDisabled={isDisabled}
						onChange={(transform) => onChange(field, { ...rule, transform })}
					/>
				) : null}
			</td>
			<td className="py-2 pr-2 break-all">{rule ? previewValueLabel(previewEntry) : 'Not mapped'}</td>
			<td className="py-2 text-ds-sm text-ds-text-muted">{isEmail ? 'Required' : null}</td>
		</tr>
	)
}

/**
 * The field mapping table (REQ-203): one row per TODAY field in display
 * order, with the IdP attribute, transform, the preview's value or outcome,
 * and the required flag. An unmapped Email is highlighted; the page disables
 * Save meanwhile. Pure and controlled.
 */
export function FieldMappingTable({ fields, preview, errors = {}, ...rowProps }: FieldMappingTableProps) {
	return (
		<table className="w-full text-ds-sm" aria-label="Field mapping">
			<thead>
				<tr className="text-left text-ds-text-muted">
					<th className="py-1 pr-2 font-medium">TODAY field</th>
					<th className="py-1 pr-2 font-medium">IdP attribute</th>
					<th className="py-1 pr-2 font-medium">Transform</th>
					<th className="py-1 pr-2 font-medium">Preview value</th>
					<th className="py-1 font-medium">Required?</th>
				</tr>
			</thead>
			<tbody>
				{MAPPED_USER_FIELDS.map((field) => (
					<FieldRow
						key={field}
						field={field}
						rule={fields[field]}
						previewEntry={preview?.find((entry) => entry.field === field)}
						error={errors[field]}
						{...rowProps}
					/>
				))}
			</tbody>
		</table>
	)
}
